import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation } from 'react-relay';
import { MutationParameters, PayloadError } from 'relay-runtime';
import { v4 as uuidv4 } from 'uuid';

import { checkAssigneeData } from '../functions';
import { CreateTaskMutation$data } from '../graphql/__generated__/CreateTaskMutation.graphql';
import { TaskTypeInput } from '../graphql/__generated__/UpdateTaskMutation.graphql';
import { UpdateTaskMutation$data } from '../graphql/__generated__/UpdateTaskMutation.graphql';
import CreateTaskMutation from '../graphql/mutations/CreateTaskMutation';
import { ComponentsDispatch, RootState } from '../store';
import { setterCalendar } from '../store/slices/calendarSlice';
import { setterCalendarTasks } from '../store/slices/calendarTasksSlice';
import { changeUpdateTaskFetchKey } from '../store/slices/settingSlice';
import { addSpinnerItem, removeSpinnerItem } from '../store/slices/spinnerSlice';
import { DayWeekType, ItemTaskType } from '../types';
import useTasks from './useTasks';

const useCommitTask = (typeDayWeek: DayWeekType) => {
    const dispatch = useDispatch<ComponentsDispatch>();
    const [commitCreate, isInFlightCreate] = useMutation(CreateTaskMutation);
    const listTask = useTasks(typeDayWeek);
    const connectionTaskId = useSelector<RootState, string>((state) => state.calendar.connectionTaskId);
    const currentTask = useSelector<RootState, ItemTaskType | null>((state) => state.calendar.currentTask);
    const [lastAddingTaskId, setLastAddingTaskId] = useState('');

    useEffect(() => {
        if (isInFlightCreate) {
            dispatch(addSpinnerItem('createTask'));
        } else {
            dispatch(removeSpinnerItem('createTask'));
        }
    }, [isInFlightCreate]);

    useEffect(() => {
        const lastAddingTask = listTask.find((task) => task.id === lastAddingTaskId);
        if (lastAddingTask) {
            switchCurrentTask(lastAddingTask);
            setLastAddingTaskId('');
        }
    }, [listTask]);

    const switchCurrentTask = (task: ItemTaskType) => {
        if (task.id !== currentTask?.id) {
            dispatch(
                setterCalendar({
                    currentTask: task,
                }),
            );
        } else if (task.id === currentTask?.id) {
            dispatch(
                setterCalendar({
                    currentTask: null,
                }),
            );
        }
    };

    const getCommitTask = useMemo(
        () =>
            (
                data: TaskTypeInput,
                connectionId: string,
                id?: string,
                successAdd?: (id: string) => void,
            ): MutationParameters => {
                delete (data as any).key;
                delete (data as any).__id;
                delete (data as any).remember; // ToDo should be delete after implementing remember functionality
                delete (data as any).__typename;
                delete (data as any).typeItem;
                delete (data as any).statusItem;
                delete (data as any).project;
                delete (data as any).start_format_datetime;
                delete (data as any).end_format_datetime;
                delete (data as any).duration;
                delete (data as any).scheduleItem;
                delete (data as any).row;
                delete (data as any).height;
                delete (data as any).col;
                delete (data as any).colShow;
                delete (data as any).colCount;
                delete (data as any).zIndex;
                delete (data as any).marginLeft;

                // const optimisticUpdateTask = {
                //     updateTask: {
                //         id,
                //         ...data,
                //     },
                // };

                const setValueToStore = (oldTask: any, data: TaskTypeInput) => {
                    const fields: (keyof ItemTaskType)[] = [
                        'datetime',
                        'start_datetime',
                        'end_datetime',
                        'schedule_item_id',
                        'assignee_for_task',
                        'project_id',
                        'owner_task_id',
                        'phase_id',
                        'color',
                        'division_id',
                        'cost_code_id',
                        'contract_invoice',
                        'wo_si',
                        'type',
                        'name',
                        'status',
                        'sub_status',
                        'priority',
                        'number',
                        'description',
                        'value_labour',
                        'value_equipment',
                        'value_material',
                        'value_other',
                        'value_total',
                    ];

                    for (const keyField of fields) {
                        //@ts-ignore
                        oldTask.setValue(data[keyField], keyField);
                    }
                };

                const optimisticUpdater = (store: any) => {
                    if (id) {
                        const oldTask = store.get(id);
                        if (oldTask) {
                            setValueToStore(oldTask, data);
                        }
                    }
                };

                return {
                    variables: {
                        input: {
                            ...data,
                            id,
                        },
                        connections: [connectionId],
                    },
                    // optimisticResponse: id ? optimisticUpdateTask : undefined,
                    onCompleted: (
                        response: CreateTaskMutation$data | UpdateTaskMutation$data,
                        errors: PayloadError[],
                    ) => {
                        if (errors) {
                            console.log(
                                (
                                    errors[0] as PayloadError & {
                                        extensions: { debugMessage: string };
                                    }
                                ).extensions.debugMessage,
                                errors,
                            );
                        } else {
                            console.log('Task save successful', response);
                            if (id) {
                                dispatch(changeUpdateTaskFetchKey());
                            }
                            successAdd?.(id || (response as CreateTaskMutation$data).createTask?.node?.id!);
                            dispatch(setterCalendarTasks({ externalTableTaskPrefixFetchKey: uuidv4() }));
                        }
                    },
                    optimisticUpdater: id ? optimisticUpdater : undefined,
                    onError: (error: any) => {
                        console.error('Mutation error:', error);
                    },
                } as any as MutationParameters;
            },
        [],
    );

    const getCommitTaskDelete = (id: string, connectionId: string, successDelete?: () => void): MutationParameters => {
        return {
            variables: {
                id,
                connections: [connectionId],
            },
            onCompleted: (response: any, errors: PayloadError[]) => {
                if (errors) {
                    console.log(
                        (
                            errors[0] as PayloadError & {
                                extensions: { debugMessage: string };
                            }
                        ).extensions.debugMessage,
                        errors,
                    );
                } else {
                    console.log('Item delete successful', response);
                    successDelete?.();
                    dispatch(setterCalendarTasks({ externalTableTaskPrefixFetchKey: uuidv4() }));
                }
            },
            onError: (error: any) => {
                console.error('Mutation error:', error);
            },
        } as any as MutationParameters;
    };

    const addTaskToDataBase = (newTask: ItemTaskType) => {
        const afterAdding = (id: string) => {
            setLastAddingTaskId(id);
            dispatch(setterCalendar({ currentDate: newTask.datetime }));
        };

        commitCreate(
            getCommitTask(
                {
                    ...newTask,
                    assignee_for_task: checkAssigneeData(newTask.assignee_for_task).join(','),
                } as TaskTypeInput,
                connectionTaskId,
                undefined,
                afterAdding,
            ),
        );
    };

    return {
        getCommitTask,
        getCommitTaskDelete,
        addTaskToDataBase,
        switchCurrentTask,
    };
};

export default useCommitTask;
